import {
  DRAFT_FIELDS,
  DRAFT_STAGE_LABEL,
  centsToDollarsInput,
  draftPrices,
  draftStage,
  fetchLotCaptures,
  fetchLotDraft,
  listingForPrice,
  PRICE_LISTING_TEXT,
  SHOW_ON_LISTING_LABEL,
  rerunLotResearch,
  researchState,
  suggestionPatch,
  unsureSuggestions,
  type DraftField,
  type DraftStage,
  type HouseCategory,
  type LotDescriptivePatch,
  type LotCapture,
  type LotDraft,
  type ListingPriceSettings,
  type PriceKey,
} from '@auctisync/api-client';
import { Badge, Button } from '@auctisync/ui';
import { useCallback, useEffect, useRef, useState } from 'react';

import { filledAfterPageLoad } from '../lib/lotDraftForm';
import { ListingDefaultsLink } from './ListingPriceChoices';
import { supabase } from '../lib/supabase';

/**
 * The cataloguing AI draft of a lot, on the office's lot page.
 *
 * Jim, 2026-09-23: when a draft is ready, the SERVER has already filled the
 * blank descriptive fields the AI was sure of (70/100 or more) — the office
 * sees them in the form below like any other value. What it was less sure of
 * is listed here, to take into the form or ignore. PRICES wait for a person, and this is where the office
 * accepts them. The rules are `@auctisync/api-client`'s, shared with the
 * Cataloging app, so "Prices to review" means the same thing on both.
 *
 * Renders nothing for a lot that never went through the cataloguing pipeline:
 * an empty panel on every other lot would be a feature that is not there.
 *
 * ONE SAVE (Jim, 2026-10-06): this panel has no Save of its own. The prices a
 * person reviews here are the lot form's own price fields — the same boxes
 * further down the page, shown next to what the AI suggested — and the page's
 * "Save changes" writes them, stamping the draft reviewed as it goes. Until
 * then the panel proposed its prices through a button of its own, so a person
 * had to press that AND the page's Save, and the two could disagree.
 *
 * RESEARCH AGAIN (Jim, 2026-10-05) — the same door as the Cataloging app
 * (`rerunLotResearch`): one paid AI call, confirmed first. The new run never
 * replaces a value a person entered, nor a price; the server decides that.
 * The status words are the app's too ("AI research in progress" / "complete").
 */

const PRICE_FIELDS = DRAFT_FIELDS.filter((f) => f.group === 'pricing');

// In progress is yellow and done is green, as on the warehouse phone. Prices
// waiting for the office take the house colour: it is the one stage that
// needs someone here to act.
const STAGE_BADGE: Record<DraftStage, 'secondary' | 'warning' | 'success' | 'error' | 'default'> = {
  not_sent: 'secondary',
  queued: 'warning',
  drafting: 'warning',
  ready: 'success',
  prices_to_review: 'default',
  failed: 'error',
};

export interface LotDraftPanelProps {
  lotId: string;
  lotStatus: string;
  /** When the page read the lot. A draft that filled fields after this is not in the form yet. */
  loadedAt: string;
  /**
   * The AI's prices, offered once a draft is waiting for review: the page puts
   * them in its form, where the person edits them and Save writes them.
   */
  onPricesProposed: (prices: Partial<Record<PriceKey, number>>) => void;
  /**
   * Whether this lot's prices are still waiting for a person. The page's Save
   * stamps the draft reviewed when they are.
   */
  onPricesPending: (pending: boolean) => void;
  /** A price as the open form holds it, in dollars — the panel shows the form. */
  priceValue: (price: PriceKey) => string;
  /** The person typed a price. It goes to the form; the page's Save writes it. */
  onPriceChange: (price: PriceKey, value: string) => void;
  /** Re-read the lot — offered when the AI filled fields after the page loaded. */
  onReload: () => void;
  /** The house's categories, to name a suggested one and to place it. */
  categories: HouseCategory[];
  /** Whether the open form has nothing in this field. */
  isBlank: (field: DraftField) => boolean;
  /** Put a suggestion into the form. The person saves it with the rest. */
  onUseSuggestion: (patch: LotDescriptivePatch) => void;
  /** The lot's "Show on listing" boxes (Jim, 2026-10-06). */
  listing: ListingPriceSettings;
  /** Tick or untick — saved at once by the page. */
  onListingChange: (patch: Partial<ListingPriceSettings>) => void;
}

export function LotDraftPanel({
  lotId,
  lotStatus,
  loadedAt,
  onPricesProposed,
  onPricesPending,
  priceValue,
  onPriceChange,
  onReload,
  categories,
  isBlank,
  onUseSuggestion,
  listing,
  onListingChange,
}: LotDraftPanelProps) {
  const [draft, setDraft] = useState<LotDraft | null>(null);
  const [capture, setCapture] = useState<LotCapture | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const load = useCallback(async () => {
    const [d, caps] = await Promise.all([
      fetchLotDraft(supabase, lotId),
      fetchLotCaptures(supabase, [lotId]),
    ]);
    setDraft(d);
    setCapture(caps.get(lotId) ?? null);
    setLoaded(true);
  }, [lotId]);

  useEffect(() => {
    void load().catch(() => {
      setLoaded(true);
    });
  }, [load]);

  const stage = draftStage(draft);

  // While the AI is on it, look again now and then.
  useEffect(() => {
    if (stage !== 'queued' && stage !== 'drafting') return;
    const t = setInterval(() => void load(), 20_000);
    return () => {
      clearInterval(t);
    };
  }, [stage, load]);

  const research = researchState(draft);

  const suggested = draftPrices(draft);
  const unsure = unsureSuggestions(draft, isBlank, categories);
  const live = lotStatus !== 'draft' && lotStatus !== 'scheduled';
  const filledAfterLoad = filledAfterPageLoad(draft, loadedAt);
  const pricesPending = stage === 'prices_to_review' && !live;

  // The page's Save stamps the draft reviewed only while prices are pending.
  useEffect(() => {
    onPricesPending(pricesPending);
  }, [pricesPending, onPricesPending]);

  /**
   * The AI's prices go into the form ONCE, when a draft first comes up for
   * review — what the old "Put these prices on the lot" button did on click,
   * minus the click. Once only: the panel re-reads itself while the AI works,
   * and a second fill would wipe a price the person had just typed.
   */
  const seeded = useRef<string | null>(null);
  useEffect(() => {
    if (!pricesPending || seeded.current === lotId) return;
    seeded.current = lotId;
    const proposed: Partial<Record<PriceKey, number>> = {};
    for (const f of PRICE_FIELDS) {
      const key = f.draftKey as PriceKey;
      const cents = suggested[key];
      if (cents !== null) proposed[key] = cents;
    }
    onPricesProposed(proposed);
  }, [pricesPending, lotId, suggested, onPricesProposed]);

  if (!loaded || (!draft && !capture)) return null;

  const rerun = async () => {
    if (
      !window.confirm(
        'Run AI research again on this lot? It can replace what the AI filled in before. Anything a person typed or changed stays, and prices still wait for a person.',
      )
    ) {
      return;
    }
    setBusy(true);
    setMessage(null);
    const res = await rerunLotResearch(supabase, lotId);
    setBusy(false);
    if (!res.ok) {
      setMessage({ tone: 'error', text: res.message });
      return;
    }
    setMessage({
      tone: 'ok',
      text:
        res.state === 'already_in_progress'
          ? 'AI research is already running on this lot.'
          : // When it lands, the "filled in after this page opened" line offers the reload.
            'AI research started.',
    });
    await load();
  };

  return (
    <section
      data-testid="lot-draft-panel"
      style={{
        border: '1px solid var(--admin-hairline, var(--border))',
        borderRadius: 'var(--r-card, 0.5rem)',
        background: 'var(--surface-elev)',
        boxShadow: 'var(--elev-1)',
        padding: '1.1rem 1.25rem',
        display: 'grid',
        gap: '0.75rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
        <h2
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '0.8rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--text-muted)',
          }}
        >
          From cataloguing
        </h2>
        <Badge variant={STAGE_BADGE[stage]} data-testid="lot-draft-stage">
          {DRAFT_STAGE_LABEL[stage]}
        </Badge>
        {capture?.send_to_inventory ? <Badge variant="outline">Flagged for inventory</Badge> : null}
        {draft && research !== 'in_progress' && !live ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => void rerun()}
            data-testid="lot-draft-rerun"
          >
            Run AI research again
          </Button>
        ) : null}
      </div>

      {capture?.notes ? (
        <p style={{ fontSize: '0.9rem' }} data-testid="lot-draft-note">
          <strong>Cataloguer’s note:</strong> {capture.notes}
        </p>
      ) : null}

      {filledAfterLoad ? (
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.85rem' }}
          data-testid="lot-draft-stale"
        >
          <span>
            AI research filled in fields after this page opened. Reload before saving, or Save will
            undo them.
          </span>
          <Button type="button" variant="outline" onClick={onReload}>
            Reload
          </Button>
        </div>
      ) : null}

      {draft && draft.autofilled_fields.length > 0 ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
          AI research filled in:{' '}
          {draft.autofilled_fields
            .map((k) => DRAFT_FIELDS.find((f) => f.key === k)?.label ?? k)
            .join(', ')}
          . Anything a person typed was left alone.
        </p>
      ) : null}

      {draft && unsure.length > 0 ? (
        <div style={{ display: 'grid', gap: '0.35rem' }}>
          <p style={{ fontSize: '0.85rem', margin: 0 }}>
            <strong>Not sure enough to fill in.</strong> The AI left these blank. Use one to put it
            in the form below, then save.
          </p>
          {unsure.map((s) => {
            const patch = suggestionPatch(draft, s.field, categories);
            return (
              <div
                key={s.field.key}
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                  fontSize: '0.85rem',
                }}
              >
                <span style={{ minWidth: '7rem', color: 'var(--text-muted)' }}>
                  {s.field.label}
                </span>
                <span style={{ flex: 1 }}>
                  {s.value}
                  {s.confidence !== null ? (
                    <span style={{ color: 'var(--text-muted)' }}>
                      {' '}
                      · {String(s.confidence)}/100
                    </span>
                  ) : null}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={patch === null}
                  onClick={() => {
                    if (patch) onUseSuggestion(patch);
                  }}
                >
                  Use
                </Button>
              </div>
            );
          })}
        </div>
      ) : null}

      {draft?.autofill_error ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--semantic-error)' }}>
          The research arrived but couldn’t fill the lot: {draft.autofill_error}
        </p>
      ) : null}

      {stage === 'failed' && draft?.last_error ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--semantic-error)' }}>
          AI research didn’t finish after {String(draft.attempts)} tries: {draft.last_error}
        </p>
      ) : null}

      {draft?.identification_notes ? (
        <p style={{ fontSize: '0.85rem' }}>
          <strong>Identified from:</strong> {draft.identification_notes}
        </p>
      ) : null}

      {stage === 'prices_to_review' || stage === 'ready' ? (
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          {draft?.pricing_logic ? (
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{draft.pricing_logic}</p>
          ) : null}
          <table style={{ width: '100%', fontSize: '0.9rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                <th style={{ padding: '0.25rem 0' }}>Price</th>
                <th style={{ padding: '0.25rem 0' }}>AI suggests</th>
                {stage === 'prices_to_review' && !live ? (
                  <th style={{ padding: '0.25rem 0' }}>Put on the lot</th>
                ) : null}
                <th style={{ padding: '0.25rem 0' }}>{SHOW_ON_LISTING_LABEL}</th>
              </tr>
            </thead>
            <tbody>
              {PRICE_FIELDS.map((f) => {
                const key = f.draftKey as PriceKey;
                const ai = suggested[key];
                return (
                  <tr key={f.key}>
                    <td style={{ padding: '0.25rem 0' }}>{f.label}</td>
                    <td style={{ padding: '0.25rem 0' }}>
                      {ai === null ? '—' : `$${centsToDollarsInput(ai)}`}
                    </td>
                    {stage === 'prices_to_review' && !live ? (
                      <td style={{ padding: '0.25rem 0' }}>
                        <input
                          aria-label={`${f.label} to put on the lot`}
                          data-testid={`lot-draft-price-${f.key}`}
                          inputMode="decimal"
                          value={priceValue(key)}
                          onChange={(e) => {
                            onPriceChange(key, e.target.value);
                          }}
                          style={{
                            width: '8rem',
                            padding: '0.3rem 0.5rem',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--r-control, 0.375rem)',
                            background: 'var(--surface)',
                            color: 'var(--text)',
                          }}
                        />
                      </td>
                    ) : null}
                    <td style={{ padding: '0.25rem 0' }}>
                      <ShowOnListing
                        price={key}
                        label={f.label}
                        listing={listing}
                        onChange={onListingChange}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <ListingDefaultsLink />
          {stage === 'prices_to_review' ? (
            live ? (
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                This lot is live. Prices can’t change while people are bidding.
              </p>
            ) : (
              // One Save for the page (Jim, 2026-10-06) — this panel has none.
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                These prices and boxes are part of the lot. <strong>Save changes</strong> puts them
                on it.
              </p>
            )
          ) : null}
        </div>
      ) : null}

      {message ? (
        <p
          data-testid="lot-draft-message"
          style={{
            fontSize: '0.85rem',
            color: message.tone === 'ok' ? 'var(--semantic-success)' : 'var(--semantic-error)',
          }}
        >
          {message.text}
        </p>
      ) : null}
    </section>
  );
}

/**
 * The "Show on listing" cell beside one reviewed price: a box for MSRP,
 * retail and the estimate (both ends share one), "Always" for the starting
 * bid, "Never" for the reserve — `listingForPrice` decides, as in the app.
 */
function ShowOnListing({
  price,
  label,
  listing,
  onChange,
}: {
  price: PriceKey;
  label: string;
  listing: ListingPriceSettings;
  onChange: (patch: Partial<ListingPriceSettings>) => void;
}) {
  const choice = listingForPrice(price);
  if (choice === 'always' || choice === 'never') {
    return <span style={{ color: 'var(--text-muted)' }}>{PRICE_LISTING_TEXT[choice]}</span>;
  }
  return (
    <input
      type="checkbox"
      aria-label={`Show ${label} on the listing`}
      data-testid={`lot-draft-show-${price}`}
      checked={listing[choice]}
      onChange={(e) => {
        onChange({ [choice]: e.target.checked });
      }}
    />
  );
}
